import React, { forwardRef, useImperativeHandle } from 'react';
import RBSheet from 'react-native-raw-bottom-sheet';
import { ViewStyle, ScrollView, View } from 'react-native';

export interface BottomSheetModalRef {
  present: () => void;
  dismiss: () => void;
}

export interface BottomSheetModalProps {
  children: React.ReactNode;
  snapPoints?: string[];
  enablePanDownToClose?: boolean;
  onDismiss?: () => void;
  backgroundStyle?: ViewStyle;
  handleIndicatorStyle?: ViewStyle;
  style?: ViewStyle;
  index?: number;
}

/**
 * Web implementation using react-native-raw-bottom-sheet
 */
const BottomSheetModal = forwardRef<BottomSheetModalRef, BottomSheetModalProps>(
  (
    {
      children,
      snapPoints = ['60%', '90%'],
      enablePanDownToClose = true,
      onDismiss,
      backgroundStyle,
      handleIndicatorStyle,
      style,
    },
    ref
  ) => {
    const rbSheetRef = React.useRef<any>(null);

    useImperativeHandle(ref, () => ({
      present: () => {
        rbSheetRef.current?.open();
      },
      dismiss: () => {
        rbSheetRef.current?.close();
      },
    }));

    // Convert percentage snap point to height
    const getHeightFromSnapPoint = (snapPoint: string) => {
      if (snapPoint.endsWith('%')) {
        const percentage = parseInt(snapPoint);
        return (typeof window !== 'undefined' ? window.innerHeight : 600) * (percentage / 100);
      }
      return 400; // default height
    };

    const height = getHeightFromSnapPoint(snapPoints[0]);

    return (
      <RBSheet
        ref={rbSheetRef}
        height={height}
        draggable={enablePanDownToClose}
        closeOnPressMask={true}
        onClose={onDismiss}
        customStyles={{
          container: {
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            backgroundColor: backgroundStyle?.backgroundColor || '#1A1D21',
            maxWidth: 430,
            marginLeft: 'auto',
            marginRight: 'auto',
            ...style,
          },
          draggableIcon: {
            backgroundColor: handleIndicatorStyle?.backgroundColor || '#ccc',
            width: handleIndicatorStyle?.width || 40,
            height: handleIndicatorStyle?.height || 5,
          },
        }}
      >
        {children}
      </RBSheet>
    );
  }
);

BottomSheetModal.displayName = 'BottomSheetModal';

export default BottomSheetModal;

// Web equivalents for BottomSheetScrollView and BottomSheetView
export const BottomSheetScrollView: React.FC<{
  children: React.ReactNode;
  contentContainerStyle?: ViewStyle;
  keyboardShouldPersistTaps?: 'handled' | 'always' | 'never';
}> = ({ children, contentContainerStyle, keyboardShouldPersistTaps }) => (
  <ScrollView
    contentContainerStyle={contentContainerStyle}
    keyboardShouldPersistTaps={keyboardShouldPersistTaps}
  >
    {children}
  </ScrollView>
);

export const BottomSheetView: React.FC<{
  children: React.ReactNode;
  style?: ViewStyle;
}> = ({ children, style }) => (
  <View style={style}>
    {children}
  </View>
);

